import { Injectable } from '@angular/core';
import { Observable, combineLatest, from, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { RxTemplate } from '@shared/rx-templates/rx-template.model';
import { ProcedureMap } from '@shared/models/procedure-map';
import { ISectionDirtyCheckService } from '@shared/models/section-dirty-check';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { RxAttachmentsApiService } from '@shared/rx-attachments/rx-attachments-api.service';
import { RxTemplateService } from '@shared/rx-templates/rx-template.service';
import { isAttachmentsVisibleForSendTo } from '@shared/services/sections-visibility.tokens';

/**
 * providedIn: 'root' — no state, all dependencies are root-scoped.
 */
@Injectable({ providedIn: 'root' })
export class AttachmentsDirtyCheckService implements ISectionDirtyCheckService {
	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly rxAttachmentsApiService: RxAttachmentsApiService,
		private readonly rxTemplateService: RxTemplateService
	) {}

	willLoseDataFor$(targetTemplate: Readonly<RxTemplate>, _targetProcedureMap: ProcedureMap | null): Observable<boolean> {
		if (!isAttachmentsVisibleForSendTo(this.rxTemplateService.currentTemplate) || isAttachmentsVisibleForSendTo(targetTemplate)) {
			return of(false);
		}

		if (!this.shellQuery.isRxAttachmentsReady) {
			return of(true);
		}

		return combineLatest([this.shellQuery.attachmentsRxId$, this.rxAttachmentsApiService.isApiInitialized$]).pipe(
			take(1),
			switchMap(([rxId, isInit]) => {
				if (!rxId) {
					return of(false);
				}

				if (!isInit) {
					return of(true);
				}

				return from(this.rxAttachmentsApiService.getAttachmentsCount([rxId], { signal: AbortSignal.timeout(5000) })).pipe(
					map(counts => counts.some(c => c.rxId === rxId && c.attachmentsCount > 0)),
					catchError(() => of(true))
				);
			})
		);
	}
}
